import { Link } from '@tanstack/react-router'
import { CalendarPlus, ChevronDown, Edit, QrCode, Sparkles } from 'lucide-react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useListing } from '~/entities/listing/model/use-listings'
import { QrGeneratorDialog } from '~/features/ai-studio/components'
import * as m from '~/paraglide/messages'
import { getListingShareUrl } from '~/shared/lib/share'
import { Button } from '~/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/shared/ui/dropdown-menu'
import { Skeleton } from '~/shared/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/shared/ui/tooltip'

export function ListingDetailActions({ listingId }: { listingId: string }) {
  const { data: business } = useCurrentBusiness()
  const { data: listing, isLoading } = useListing(business?.id ?? '', listingId)

  if (isLoading || !business) {
    return <Skeleton className="h-9 w-full max-w-lg rounded-lg" />
  }

  if (!listing) {
    return null
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button>
            <CalendarPlus className="mr-2 h-4 w-4" />
            {m.createEvent()}
            <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem asChild>
            <Link to="/dashboard/events/create" search={{ fromListing: listingId }}>
              <CalendarPlus />
              {m.createEventManually()}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/dashboard/ai-studio/event-creator" search={{ fromListing: listingId }}>
              <Sparkles />
              {m.createEventWithAi()}
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button asChild variant="outline">
        <Link to="/dashboard/listings/$listingId/edit" params={{ listingId }}>
          <Edit className="mr-2 h-4 w-4" />
          {m.edit()}
        </Link>
      </Button>

      {listing.isDraft || listing.status === 'Draft' ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button variant="outline" disabled>
                  <QrCode className="mr-2 h-4 w-4" />
                  {m.qrCode()}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{m.publishBeforeSharing()}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <QrGeneratorDialog
          data={getListingShareUrl(listingId)}
          label={listing.name}
          triggerClassName=""
        />
      )}
    </div>
  )
}
